import React from "react";

import Box from "@mui/material/Box";
import { Link, Stack, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { fetchDailyPacksForDisplay } from "../utils";
import SharedPackLoader from "./SharedPackLoader";
import { Info } from "lucide-react";
import {
  getAccent,
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";

const PackListHeading = ({ darkMode, title, tooltip }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    gap="6px"
    sx={{ width: "100%", paddingBottom: "6px" }}
  >
    <Typography
      sx={{
        fontFamily: HEADING_FONT,
        fontWeight: 600,
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: getMutedText(darkMode),
      }}
    >
      {title}
    </Typography>
    <Tooltip title={tooltip} placement="top">
      <Info size={13} color={getMutedText(darkMode)} />
    </Tooltip>
  </Stack>
);

const PackList = ({ darkMode, packs, currentUrl }) => (
  <Box
    sx={{
      width: "198px",
      maxHeight: "212px",
      margin: "0 auto",
      border: `1px solid ${getBorder(darkMode)}`,
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    <Box
      className="toggle-button-group-scroller"
      sx={{ maxHeight: "210px", overflowY: "auto", overflowX: "hidden" }}
    >
      {(packs || []).map(({ packTitle, packUrl }) => {
        const isCurrent = packUrl === currentUrl;
        return (
          <Tooltip title={packTitle} key={packUrl}>
            <Box
              onClick={() => {
                if (!isCurrent) {
                  window.open(`${window.location.origin}${packUrl}`).focus();
                }
              }}
              sx={{
                padding: "9px 12px",
                backgroundColor: isCurrent
                  ? darkMode
                    ? "rgba(144,202,249,0.14)"
                    : "rgba(25,118,210,0.08)"
                  : "",
                "&:hover": {
                  backgroundColor: isCurrent
                    ? ""
                    : darkMode
                    ? "rgba(144,202,249,0.08)"
                    : "rgba(25,118,210,0.05)",
                  cursor: isCurrent ? "" : "pointer",
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: "13.5px",
                  textAlign: "left",
                  width: "100%",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {packTitle}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  </Box>
);

const SharedPackDisplay = ({ darkMode }) => {
  const [fetchedPacks, setFetchedPacks] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      const response = await fetchDailyPacksForDisplay();
      setFetchedPacks(response);
      setIsLoading(false);
    })();
  }, []);

  const currentUrl = window.location.pathname;
  return (
    <Stack gap={1} marginTop="20px" alignItems="center">
      <PackListHeading
        darkMode={darkMode}
        title="Trending Packs"
        tooltip="These are public packs that are currently trending on PackWizard"
      />
      {isLoading ? (
        <Box
          sx={{
            width: "198px",
            height: "212px",
            border: `1px solid ${getBorder(darkMode)}`,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <SharedPackLoader />
        </Box>
      ) : (
        <PackList
          darkMode={darkMode}
          packs={fetchedPacks && fetchedPacks.TRENDING}
          currentUrl={currentUrl}
        />
      )}
      <Box sx={{ paddingTop: "12px", width: "100%" }}>
        <PackListHeading
          darkMode={darkMode}
          title="Most Viewed Packs"
          tooltip="These are the most viewed public packs on PackWizard"
        />
      </Box>
      {isLoading ? (
        <Box
          sx={{
            width: "198px",
            height: "212px",
            border: `1px solid ${getBorder(darkMode)}`,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <SharedPackLoader />
        </Box>
      ) : (
        <PackList
          darkMode={darkMode}
          packs={fetchedPacks && fetchedPacks.MOST_VIEWED}
          currentUrl={currentUrl}
        />
      )}
      <Link
        href="/browse"
        underline="hover"
        sx={{
          textDecoration: "none",
          fontFamily: HEADING_FONT,
          fontWeight: 600,
          fontSize: "13.5px",
          color: getAccent(darkMode),
          paddingTop: "6px",
        }}
      >
        Browse More Packs
      </Link>
    </Stack>
  );
};

export default SharedPackDisplay;
